import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, MessageSquare, Save } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import API_URL from "../../config";

const InquiryViewPage = () => {
  const FOLLOWUPS_PAGE_SIZE = 4;
  const { inquiryId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [inquiry, setInquiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [followUpSubmitting, setFollowUpSubmitting] = useState(false);

  const [editStatus, setEditStatus] = useState("New");
  const [editPriority, setEditPriority] = useState("Medium");
  const [followUpDate, setFollowUpDate] = useState("");
  const [followUpNote, setFollowUpNote] = useState("");
  const [followUpPage, setFollowUpPage] = useState(1);

  const loggedInCreatedBy =
    user?.username || user?.name || user?.fullName || user?.email || "";

  const fetchInquiry = async () => {
    const mapUniversityToInquiry = (u) => ({
      _id: u._id,
      institutionName: u.university_name || "-",
      institutionType: u.institution_type || "-",
      websiteUrl: u.university_website || "",
      country: u.country || "-",
      state: u.state || "-",
      contactPersonName: u.contact_person_name || "-",
      officialEmail: u.university_email || "-",
      contactNumber: u.primary_contact || "-",
      status: u.status || "New",
      priority: u.priority || "Medium",
      createdAt: u.createdAt || new Date().toISOString(),
      updatedAt: u.updatedAt || new Date().toISOString(),
      followUps: Array.isArray(u.followUps) ? u.followUps : [],
    });

    try {
      setLoading(true);
      setError("");
      const response = await fetch(`${API_URL}/api/auth/universities/${inquiryId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch inquiry: ${response.status}`);
      }
      const payload = await response.json();
      const data = payload?.university ? mapUniversityToInquiry(payload.university) : null;
      setInquiry(data);
      if (data) {
        setEditStatus(data.status || "New");
        setEditPriority(data.priority || "Medium");
      }
    } catch (err) {
      setError(err.message || "Failed to load inquiry");
      setInquiry(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiry();
  }, [inquiryId]);

  const handleSave = async (e) => {
    e.preventDefault();

    try {
      setFollowUpSubmitting(true);

      const updateResponse = await fetch(`${API_URL}/api/auth/universities/${inquiryId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          status: editStatus,
          priority: editPriority,
        }),
      });

      if (!updateResponse.ok) {
        throw new Error(`Failed to update inquiry: ${updateResponse.status}`);
      }

      if (followUpNote.trim()) {
        const followUpResponse = await fetch(`${API_URL}/api/auth/universities/${inquiryId}/followups`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            note: followUpNote.trim(),
            createdBy: loggedInCreatedBy || "",
            nextFollowUpDate: followUpDate || null,
          }),
        });

        if (!followUpResponse.ok) {
          throw new Error(`Failed to add follow-up: ${followUpResponse.status}`);
        }
      }

      await fetchInquiry();
      setFollowUpDate("");
      setFollowUpNote("");
      setShowUpdateModal(false);
      setError("");
    } catch (err) {
      setError(err.message || "Failed to save inquiry updates");
    } finally {
      setFollowUpSubmitting(false);
    }
  };

  const sortedFollowUps = useMemo(() => {
    return (inquiry?.followUps || []).slice().reverse();
  }, [inquiry]);

  const totalFollowUpPages = Math.max(1, Math.ceil(sortedFollowUps.length / FOLLOWUPS_PAGE_SIZE));
  const paginatedFollowUps = useMemo(() => {
    const start = (followUpPage - 1) * FOLLOWUPS_PAGE_SIZE;
    return sortedFollowUps.slice(start, start + FOLLOWUPS_PAGE_SIZE);
  }, [sortedFollowUps, followUpPage]);

  useEffect(() => {
    if (followUpPage > totalFollowUpPages) {
      setFollowUpPage(totalFollowUpPages);
    }
  }, [followUpPage, totalFollowUpPages]);

  if (loading) {
    return <div className="p-5">Loading inquiry details...</div>;
  }

  if (error) {
    return <div className="p-5 text-danger">{error}</div>;
  }

  if (!inquiry) {
    return <div className="p-5 text-danger">Inquiry not found.</div>;
  }

  const todayDate = new Date().toISOString().split("T")[0];

  return (
    <div className="container-fluid py-4">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h1 className="h4 fw-bold mb-1">Institution Inquiry</h1>
          <p className="mb-0 text-muted">
            View and update institution inquiry details.
          </p>
        </div>
        <button
          className="btn btn-primary d-flex align-items-center gap-2"
          onClick={() => navigate("/inquiries")}
        >
          <ArrowLeft size={16} />
          Back to List
        </button>
      </div>

      <div className="row g-4 mb-4">
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm">
            <div className="card-body p-4">
              <h3 className="h6 fw-bold mb-4">Inquiry Details</h3>
              <div className="row g-3">
                <InfoField label="Institution / Organization" value={inquiry.institutionName} />
                <InfoField label="Institution Type" value={inquiry.institutionType} />
                <InfoField label="Website URL" value={inquiry.websiteUrl || "-"} />
                <InfoField label="Country" value={inquiry.country} />
                <InfoField label="State / Province" value={inquiry.state} />
                <InfoField label="Contact Person Name" value={inquiry.contactPersonName} />
                <InfoField label="Official Email" value={inquiry.officialEmail} />
                <InfoField label="Contact Number (WhatsApp)" value={inquiry.contactNumber} />
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body p-4">
              <h3 className="h6 fw-bold mb-4">Quick Info</h3>
              <QuickInfoRow
                label="Status"
                value={<span className={`badge bg-${inquiry.status === "New" ? "success" : "warning"}`}>{inquiry.status}</span>}
              />
              <QuickInfoRow
                label="Priority"
                value={<span className={`badge bg-${inquiry.priority === "High" ? "danger" : "info"}`}>{inquiry.priority}</span>}
              />
              <QuickInfoRow label="Created" value={new Date(inquiry.createdAt).toLocaleString()} />
              <QuickInfoRow label="Updated" value={new Date(inquiry.updatedAt).toLocaleString()} />
              <QuickInfoRow label="Follow-ups" value={inquiry.followUps?.length || 0} />
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body p-4">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h2 className="h6 fw-bold d-flex align-items-center gap-2 mb-0">
              <MessageSquare size={18} className="text-primary" />
              Follow-up Timeline
            </h2>
            <button
              type="button"
              onClick={() => setShowUpdateModal(true)}
              className="btn btn-primary"
            >
              Update Inquiry
            </button>
          </div>

          {(!inquiry.followUps || inquiry.followUps.length === 0) ? (
            <p className="text-muted">No updates added yet.</p>
          ) : (
            <>
              {paginatedFollowUps.map((item) => (
                <div
                  key={item._id}
                  className="mb-3 p-3 rounded-3"
                  style={{ backgroundColor: "#f8fafc", border: "1px solid var(--border-color)" }}
                >
                  <div className="d-flex justify-content-between">
                    <span className="fw-semibold text-dark">
                      <Clock size={14} className="d-inline-block me-1" />
                      {new Date(item.date).toLocaleString()}
                    </span>
                    <span className="text-muted" style={{ fontSize: "0.8rem" }}>
                      By: {item.createdBy}
                    </span>
                  </div>
                  <p className="mt-2 mb-0 text-secondary">{item.note}</p>
                  {item.nextFollowUpDate && (
                    <p className="mt-2 mb-0 text-muted" style={{ fontSize: "0.8rem" }}>
                      <Calendar size={11} className="d-inline-block me-1" />
                      Next Follow-up: {new Date(item.nextFollowUpDate).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ))}
              {sortedFollowUps.length > 0 && (
                <div className="d-flex align-items-center justify-content-between mt-3">
                  <small className="text-muted">
                    Showing {(followUpPage - 1) * FOLLOWUPS_PAGE_SIZE + 1} to {Math.min(followUpPage * FOLLOWUPS_PAGE_SIZE, sortedFollowUps.length)} of {sortedFollowUps.length}
                  </small>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      disabled={followUpPage === 1}
                      onClick={() => setFollowUpPage((prev) => Math.max(prev - 1, 1))}
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      disabled={followUpPage === totalFollowUpPages}
                      onClick={() => setFollowUpPage((prev) => Math.min(prev + 1, totalFollowUpPages))}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showUpdateModal && (
        <div
          className="modal show"
          style={{
            display: "flex",
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            backgroundColor: "rgba(15, 23, 42, 0.4)",
            backdropFilter: "blur(2px)",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
            overflowY: "auto",
          }}
        >
          <div className="modal-dialog modal-lg" style={{ margin: 0, width: "100%", maxWidth: "760px" }}>
            <div className="modal-content" style={{ backgroundColor: "var(--card-bg)", color: "var(--text-primary)", border: "1px solid var(--border-color)" }}>
              <div className="modal-header" style={{ borderBottom: "1px solid var(--border-color)" }}>
                <h5 className="modal-title">Update Inquiry Details</h5>
                <button type="button" className="btn-close" onClick={() => setShowUpdateModal(false)}></button>
              </div>
              <form onSubmit={handleSave}>
                <div className="modal-body">
                  <div className="row g-3">
                    <MetaBox label="Today's Date" value={todayDate} />
                    <MetaBox label="Created By" value={loggedInCreatedBy || "-"} />

                    <div className="col-sm-6">
                      <label className="form-label text-secondary">Status</label>
                      <select
                        value={editStatus}
                        onChange={(e) => setEditStatus(e.target.value)}
                        className="form-select"
                      >
                        <option value="New">New</option>
                        <option value="Contacted">Contacted</option>
                        <option value="Qualified">Qualified</option>
                        <option value="Lost">Lost</option>
                      </select>
                    </div>

                    <div className="col-sm-6">
                      <label className="form-label text-secondary">Priority</label>
                      <select
                        value={editPriority}
                        onChange={(e) => setEditPriority(e.target.value)}
                        className="form-select"
                      >
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                      </select>
                    </div>

                    <div className="col-sm-6">
                      <label className="form-label text-secondary">Next Follow-up Date</label>
                      <input
                        type="date"
                        min={todayDate}
                        value={followUpDate}
                        onChange={(e) => setFollowUpDate(e.target.value)}
                        className="form-control"
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label text-secondary">Follow-up Remarks</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        placeholder="Write remarks (optional)..."
                        value={followUpNote}
                        onChange={(e) => setFollowUpNote(e.target.value)}
                      ></textarea>
                    </div>
                  </div>
                </div>
                <div className="modal-footer" style={{ borderTop: "1px solid var(--border-color)" }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowUpdateModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary d-flex align-items-center gap-2" disabled={followUpSubmitting}>
                    {followUpSubmitting ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    ) : (
                      <Save size={16} />
                    )}
                    {followUpSubmitting ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoField = ({ label, value }) => (
  <div className="col-md-6">
    <p className="mb-1 text-muted" style={{ fontSize: "0.82rem" }}>{label}</p>
    <p className="mb-0 fw-semibold text-dark text-break" style={{ overflowWrap: "anywhere" }}>
      {value || "-"}
    </p>
  </div>
);

const QuickInfoRow = ({ label, value }) => (
  <div className="d-flex justify-content-between align-items-center py-1">
    <span className="text-muted">{label}</span>
    <span className="fw-semibold text-dark text-end" style={{ maxWidth: "62%", overflowWrap: "anywhere" }}>
      {value}
    </span>
  </div>
);

const MetaBox = ({ label, value }) => (
  <div className="col-sm-6">
    <div className="p-3 rounded" style={{ backgroundColor: "#f8fafc", border: "1px solid var(--border-color)" }}>
      <p className="mb-0 text-muted" style={{ fontSize: "0.8rem" }}>{label}</p>
      <p className="fw-semibold mb-0 text-dark text-break" style={{ overflowWrap: "anywhere", lineHeight: 1.3 }}>
        {value}
      </p>
    </div>
  </div>
);

export default InquiryViewPage;
